import type { DocumentNode, IntrospectionQuery } from 'graphql';
import { Kind, buildClientSchema } from 'graphql';
import { print } from 'graphql/language/printer';

// Outside local dev environment, queries are optimized and transformed to stored operations
// Meaning the query used in production are very different from the ones used in development
// Preprocessing the query before sending it to the server allows us to use the same query in both environments

export const optimizeQuery = {
  current: <T extends DocumentNode>(query: T): T => query,
};

// A document carries the same fragment twice when two spread paths reach it
// through different module instances — dev serves one file under several
// specifiers, so each instance builds its own document object and `gql` cannot
// dedupe them by identity. Relay's parser rejects the result outright, while
// Apollo executes it fine. Drop the extra copies, but only byte-identical ones:
// two same-named fragments that actually differ is a bug worth surfacing.
export const withUniqueFragments = (query: DocumentNode): DocumentNode => {
  const sourceByName = new Map<string, string>();

  const definitions = query.definitions.filter(definition => {
    if (definition.kind !== Kind.FRAGMENT_DEFINITION) return true;

    const { value: name } = definition.name;
    const source = print(definition);
    const kept = sourceByName.get(name);
    if (kept === undefined) {
      sourceByName.set(name, source);
      return true;
    }
    if (kept !== source) {
      throw new Error(
        `Conflicting definitions for fragment ${name} in the same document`
      );
    }
    return false;
  });

  return definitions.length === query.definitions.length
    ? query
    : { ...query, definitions };
};

export const initializeOptimizeQuery = async (callback: () => void) => {
  if (import.meta.env.MODE !== 'development') {
    callback();
    return;
  }
  // HACK https://github.com/dotansimha/graphql-code-generator/discussions/8784
  process.hrtime = process.hrtime || (() => [0, 0]);
  const { optimizeDocuments } =
    await import('@graphql-tools/relay-operation-optimizer');
  // HACK use import.meta.glob to avoid loading this file in typescript
  const { default: schemaJson } = await import.meta
    .glob<Record<'default', unknown>>('../../../graphql-schema.json', {
      eager: false,
    })
    ['../../../graphql-schema.json']();

  const schema = schemaJson as unknown as IntrospectionQuery;
  const clientSchema = buildClientSchema(schema);
  const optimizedQueries: Record<string, DocumentNode> = {};

  optimizeQuery.current = <T extends DocumentNode>(query: T): T => {
    const s = print(query);
    if (!optimizedQueries[s]) {
      // eslint-disable-next-line prefer-destructuring
      optimizedQueries[s] = optimizeDocuments(clientSchema, [
        withUniqueFragments(query),
      ])[0];
    }
    return optimizedQueries[s] as T;
  };
  callback();
};
